import { useEffect, useState } from 'react';
import * as MyKeysApiLogin from '../core/apis/mykeys/login';
import * as Exceptions from '../core/apis/mykeys/exceptions';
import { MyKeysPromiseMap } from '../core/apis/mykeys/mappings';
import { AppKeys, newMsg } from '../core/localization';
import { ErrorDataResponse } from '../interfaces';
import {
  CallStatus,
  CallStatusDefault,
  UserInfo,
  UserInfoDefault,
} from '../models';
import useCallStatus from './useCallStatus';
import Logger from '../libs/logger'; import { LogArea } from '../libs/logger/const';

export interface UseUserInfoData {
  userInfo: UserInfo;
  getInfoStatus: CallStatus;
}

export const UseUserInfoDataDefault: UseUserInfoData = {
  userInfo: UserInfoDefault,
  getInfoStatus: CallStatusDefault,
};

export const UseUserInfoDataDefaultByUser = (u: UserInfo): UseUserInfoData => ({
  userInfo: u || UserInfoDefault,
  getInfoStatus: CallStatusDefault,
});

export type GetInfo = (email: string) => void;

export interface UseUserInfoResult extends UseUserInfoData {
  reset: (data?: UseUserInfoData) => void;
  getInfo: GetInfo;
}

const useUserInfo = (
  initialUser = UseUserInfoDataDefault,
  userUpdated = (u: UseUserInfoData) => {}
): UseUserInfoResult => {
  const [userInfo, setUserInfo] = useState(initialUser.userInfo);
  const getCustomerCall = useCallStatus();

  const reset = (data = UseUserInfoDataDefault) => {
    setUserInfo(data.userInfo);
    getCustomerCall.reset(data.getInfoStatus);
  };

  useEffect(()=>{
    updateUserInfo(userInfo)
  },[userInfo]);

  const updateUserInfo = (userInfo: UserInfo) => {
    userUpdated({
      userInfo,
      getInfoStatus: getCustomerCall.status
    });
  };

  const msgEmailError = newMsg(AppKeys.UserDetailForm_Email_Error);
  const msgValidatingError = newMsg(AppKeys.UserDetailForm_Validate_Error);
  const msgUserNotFound = newMsg(AppKeys.UserDetailForm_UserNotFound);
  const msgUserBlocked = newMsg(AppKeys.UserDetailForm_UserLoginServerBlocked);

  const regexEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,16})+$/;
  const getCustomerOfAUser = (email: string) => {
    Logger.info(['getCustomerOfAUser', email], LogArea.Redux);
    if (!regexEmail.test(email)) {
      getCustomerCall.error(msgEmailError);
      setUserInfo({ ...UserInfoDefault });
      return;
    }
    MyKeysApiLogin.getUserDetails(email)
      .then(MyKeysPromiseMap)
      .then((data: UserInfo) => {
        getCustomerCall.success();
        setUserInfo(data);
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = msgValidatingError;
        if (Exceptions.IsUserNotFound(err)) {
          setUserInfo({ ...UserInfoDefault, email });
          errMsg = msgUserNotFound;
        } else if(Exceptions.UserLoginServerBlocked(err)) {
            errMsg = msgUserBlocked;
        }
        getCustomerCall.error(errMsg);
      });
    setUserInfo({ ...UserInfoDefault });
    getCustomerCall.fetch();
  };

  return {
    userInfo,
    getInfoStatus: getCustomerCall.status,
    getInfo: getCustomerOfAUser,
    reset,
  };
};

export default useUserInfo;
